import React from 'react';
import { CheckCircle, Business } from '@mui/icons-material';
import {
  BudgetRequest,
  BudgetRequestRecipientStatus,
  BudgetRequestRecipient,
} from '@models/budget.model';
import { useTranslation } from '@hooks/useTranslation';

interface TaskProjectInfoProps {
  activeSubTab: 'received' | 'delegated';
  isDelegatedTask: boolean;
  task: any;
  currentBudgetRequest: BudgetRequest | null;
  organizationId: string;
  isRecipient: boolean;
}

export const TaskProjectInfo: React.FC<TaskProjectInfoProps> = ({
  activeSubTab,
  isDelegatedTask,
  task,
  currentBudgetRequest,
  organizationId,
  isRecipient,
}) => {
  const tTaskDetail = useTranslation('taskDetail');
  // Sprint 28 change request (global sweep, defect fix). `recipient.status` rendered raw at 3
  // sites below (including the `yourStatus` template) — reuses the same map as the sibling
  // `BudgetRequestCard`.
  const recipientStatusTextMap: Record<string, string> = {
    [BudgetRequestRecipientStatus.SENT]: tTaskDetail.budget.recipientStatusSent,
    [BudgetRequestRecipientStatus.VIEWED]: tTaskDetail.budget.recipientStatusViewed,
    [BudgetRequestRecipientStatus.RESPONDED]: tTaskDetail.budget.recipientStatusResponded,
    [BudgetRequestRecipientStatus.ACCEPTED]: tTaskDetail.budget.recipientStatusAccepted,
    [BudgetRequestRecipientStatus.REJECTED]: tTaskDetail.budget.recipientStatusRejected,
  };
  const acceptedRecipient = currentBudgetRequest?.recipients?.find(
    (recipient: BudgetRequestRecipient) =>
      recipient.status === BudgetRequestRecipientStatus.ACCEPTED,
  );

  const currentOrgRecipient = currentBudgetRequest?.recipients?.find(
    (recipient: BudgetRequestRecipient) =>
      recipient.recipientOrganizationId === organizationId,
  );

  return (
    <div
      className={`border rounded-lg p-4 ${
        activeSubTab === 'received' && isDelegatedTask
          ? 'bg-purple-50 border-purple-200'
          : activeSubTab === 'delegated'
            ? 'bg-blue-50 border-blue-200'
            : 'bg-green-50 border-green-200'
      }`}>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {activeSubTab === 'received' && isDelegatedTask
              ? tTaskDetail.budget.originalProject
              : tTaskDetail.budget.project}
          </label>
          <p className="text-sm text-gray-900">
            {activeSubTab === 'received' && isDelegatedTask
              ? currentBudgetRequest?.task?.project?.name || tTaskDetail.budget.notAvailable
              : task?.project?.name || tTaskDetail.budget.notAvailable}
          </p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {activeSubTab === 'received' && isDelegatedTask
              ? tTaskDetail.budget.originalTask
              : tTaskDetail.budget.task}
          </label>
          <p className="text-sm text-gray-900">
            {activeSubTab === 'received' && isDelegatedTask
              ? currentBudgetRequest?.task?.name || tTaskDetail.budget.notAvailable
              : task?.name || tTaskDetail.budget.notAvailable}
          </p>
        </div>

        {activeSubTab === 'received' && isDelegatedTask && (
          <>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {tTaskDetail.budget.currentDelegatedTask}
              </label>
              <p className="text-sm text-gray-900">{task?.name || tTaskDetail.budget.notAvailable}</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {tTaskDetail.budget.delegatedProject}
              </label>
              <p className="text-sm text-gray-900">
                {task?.project?.name || tTaskDetail.budget.notAvailable}
              </p>
            </div>
          </>
        )}

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {activeSubTab === 'delegated'
              ? !isDelegatedTask
                ? acceptedRecipient
                  ? tTaskDetail.budget.delegationRecipient
                  : tTaskDetail.budget.budgetRecipients
                : acceptedRecipient
                  ? tTaskDetail.budget.subDelegationRecipient
                  : tTaskDetail.budget.budgetRecipients
              : activeSubTab === 'received' && isDelegatedTask
                ? tTaskDetail.budget.budgetOriginator
                : isRecipient
                  ? tTaskDetail.budget.originatorOrganization
                  : acceptedRecipient
                    ? tTaskDetail.budget.assignedOrganization
                    : tTaskDetail.budget.budgetRecipients}
          </label>
          <div className="flex items-start gap-2">
            <Business className="h-4 w-4 text-gray-500 mt-0.5" />
            <div className="text-sm text-gray-900">
              {activeSubTab === 'delegated'
                ? acceptedRecipient
                  ? acceptedRecipient.recipientOrganization?.name
                  : currentBudgetRequest?.recipients && currentBudgetRequest.recipients.length > 0
                    ? (
                      <div className="space-y-1">
                        {currentBudgetRequest.recipients.map((recipient: BudgetRequestRecipient) => (
                          <div key={recipient.recipientOrganizationId} className="flex items-center gap-2">
                            <span>{recipient.recipientOrganization?.name || tTaskDetail.budget.unknown}</span>
                            <span className={`px-1.5 py-0.5 text-xs font-medium rounded-full ${
                              recipient.status === BudgetRequestRecipientStatus.ACCEPTED
                                ? 'bg-green-100 text-green-800'
                                : recipient.status === BudgetRequestRecipientStatus.REJECTED
                                  ? 'bg-red-100 text-red-800'
                                  : 'bg-yellow-100 text-yellow-800'
                            }`}>
                              {recipientStatusTextMap[recipient.status] || recipient.status}
                            </span>
                          </div>
                        ))}
                      </div>
                    )
                    : tTaskDetail.budget.notAvailable
                : isDelegatedTask || isRecipient
                  ? currentBudgetRequest?.originatorOrganization?.name ||
                    tTaskDetail.budget.notAvailable
                  : acceptedRecipient
                    ? acceptedRecipient.recipientOrganization?.name
                    : currentBudgetRequest?.recipients && currentBudgetRequest.recipients.length > 0
                      ? (
                        <div className="space-y-1">
                          {currentBudgetRequest.recipients.map((recipient: BudgetRequestRecipient) => (
                            <div key={recipient.recipientOrganizationId} className="flex items-center gap-2">
                              <span>{recipient.recipientOrganization?.name || tTaskDetail.budget.unknown}</span>
                              <span className={`px-1.5 py-0.5 text-xs font-medium rounded-full ${
                                recipient.status === BudgetRequestRecipientStatus.ACCEPTED
                                  ? 'bg-green-100 text-green-800'
                                  : recipient.status === BudgetRequestRecipientStatus.REJECTED
                                    ? 'bg-red-100 text-red-800'
                                    : 'bg-yellow-100 text-yellow-800'
                              }`}>
                                {recipientStatusTextMap[recipient.status] || recipient.status}
                              </span>
                            </div>
                          ))}
                        </div>
                      )
                      : tTaskDetail.budget.notAvailable}
            </div>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {tTaskDetail.budget.status}
          </label>
          <div className="flex items-center gap-2">
            <CheckCircle className="h-4 w-4 text-green-500" />
            <span className="text-sm font-medium text-green-700">
              {activeSubTab === 'delegated'
                ? !isDelegatedTask
                  ? tTaskDetail.budget.delegationCreated
                  : tTaskDetail.budget.subDelegationCreated
                : activeSubTab === 'received' && isDelegatedTask
                  ? tTaskDetail.budget.taskCreatedFromAcceptedBid
                  : isRecipient
                    ? tTaskDetail.budget.yourStatus.replace(
                        '{status}',
                        (currentOrgRecipient?.status &&
                          recipientStatusTextMap[currentOrgRecipient.status]) ||
                          currentOrgRecipient?.status ||
                          tTaskDetail.budget.unknown,
                      )
                    : acceptedRecipient
                      ? tTaskDetail.budget.bidAcceptedBy.replace(
                          '{org}',
                          acceptedRecipient.recipientOrganization?.name || '',
                        )
                      : tTaskDetail.budget.bidRequestPending}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};